import SharedPreferences from "../../constants/SharedPreferences";
import {
  getErrorMessage,
  generateBCryptSalt,
  saveInStorage,
  getFromStorage,
  logApiResponse,
  getStorageEncryptionKey,
  saveInInternalStorage,
  getFromInternalStorage,
  handleError,
  saveEccCurvesFromResponse,
  getEncryptionCurveFromStorage,
  generateHMacHash,
} from "../../util/Utility";
import {
  getNonAuthRestClient,
  getAuthRestclient,
  getTwoFARestClient,
  getAuthRestClientForAdmin,
} from "../RestUtility";
import {
  aeadDecryptWithAssociatedData,
  aeadDecryptWithPasscode,
  aeadEncryptWithAssociatedData,
  generateAesKey,
} from "../../crypto/AuthenticatedEncryption";
import BayunError from "../../constants/BayunError";
import Constants from "../../constants/Constants";
import { AuthenticateResponse } from "../../BayunCore";
import {
  decryptAsymmetric,
  encryptAsymmetric,
} from "../../util/AsymmetricEncryption";
import {
  getMessageFromSignatureString,
  retrieveAndverifyLastSignature,
  verifyMessageForPublicKeyTag,
  verifyAndPersistOrgSettingsFromLastUpdatedAt,
  verifyOrgAppApprovalSignature,
} from "../../util/SignatureUtil";
import { derivePbkdf2Encoded, derivePbkdf2Raw, encodeKey } from "../../crypto/PBKDF2";
import { asRawKey } from "../../crypto/KeyMaterial";
import SigningPublicKeyTags from "../../constants/SigningPublicKeyTags";
import { decryptAndSaveMemberInfo } from "./internal/memberInfoCrypto";
import { verifyLockBoxResponse } from "./internal/lockBoxVerification";
import { createMemberAppKeyPair } from "./internal/memberAppKeyPair";
import { getRefreshToken } from "./internal/refreshToken";
import { getMemberInfoForSSO } from "./ssoApi";
import { getTwoFAData } from "./twoFaApi";
import { createMemberData } from "./provisioningApi";
import { setMemberLockBoxReader } from "./internal/memberStatusRefresh";

async function decryptAndStoreAppSigningKey(
  sessionId,
  appKeyPair,
  appId,
  appPrivKeyDecryptionKey,
  signingPrivateKeyPref,
  signingPublicKeyPref,
) {
  if (!appKeyPair.appSigningPrivateKey) {
    throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
  }
  // The signing private key is always sealed with the app-secret-derived key. A bare PEM on
  // the wire means the key was stored unprotected, so reject it rather than using it.
  if (appKeyPair.appSigningPrivateKey.includes("PRIVATE KEY")) {
    throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
  }
  const appSigningPrivateKey = await aeadDecryptWithAssociatedData(
    appKeyPair.appSigningPrivateKey,
    appId,
    appPrivKeyDecryptionKey,
  );
  if (!appSigningPrivateKey) {
    throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
  }
  await saveInInternalStorage(sessionId, signingPrivateKeyPref, appSigningPrivateKey);
  if (appKeyPair.appSigningPublicKey) {
    await saveInStorage(
      sessionId,
      signingPublicKeyPref,
      appKeyPair.appSigningPublicKey,
    );
  }
}

/**
 * Verifies and persists the signature verification anchors carried on a lockbox response.
 *
 * The admin and backdoor signing public keys are the anchors for every
 * Admin/BackdoorSigningPublicKeyTag verification (member public keys, backdoor key, org
 * settings). They arrive as a chain signed by the Bayun server signing key, which is itself
 * pinned by the caller, so verifying that chain here is what keeps the trust root cryptographic
 * rather than TLS-only.
 *
 * Fails closed: an unverifiable anchor aborts the login instead of being trusted.
 */
export async function prepareLockBoxVerificationKeys(sessionId, lockBoxResponse) {
  if (lockBoxResponse.adminSigningPublicKey) {
    if (
      !(await retrieveAndverifyLastSignature(
        sessionId,
        lockBoxResponse.adminSigningPublicKey,
      ))
    ) {
      console.log("adminSigningPublicKey verification failed.");
      throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
    }
    await saveInStorage(
      sessionId,
      SharedPreferences.ADMIN_SIGNING_PUBLIC_KEY,
      getMessageFromSignatureString(lockBoxResponse.adminSigningPublicKey),
    );
  }
  if (lockBoxResponse.backdoorSigningPublicKey) {
    if (
      !(await retrieveAndverifyLastSignature(
        sessionId,
        lockBoxResponse.backdoorSigningPublicKey,
      ))
    ) {
      console.log("backdoorSigningPublicKey verification failed.");
      throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
    }
    await saveInStorage(
      sessionId,
      SharedPreferences.BACKDOOR_SIGNING_PUBLIC_KEY,
      getMessageFromSignatureString(lockBoxResponse.backdoorSigningPublicKey),
    );
  }
  // Deliberately NOT seeded here:
  //   backdoorPublicKey    - verifyAndSaveBackdoorPublicKey skips when a value already exists,
  //                          so seeding it silently disabled that verification outright.
  //   userSigningPublicKey - verifyLockBoxResponse verifies its server signature chain and then
  //                          stores the stripped key; seeding it would trust it unverified first.
}


export const getAppKeyPairId = async (sessionId, failureCallback) => {
  var appKeyPairId = "";
  if (
    await getFromStorage(
      sessionId,
      SharedPreferences.AUTHORISATION_APP_KEY_PAIR_ID,
    )
  ) {
    appKeyPairId = await getFromStorage(
      sessionId,
      SharedPreferences.AUTHORISATION_APP_KEY_PAIR_ID,
    );
  } else if (
    await getFromStorage(
      sessionId,
      SharedPreferences.MEMBER_CREATION_APP_KEY_PAIR_ID,
    )
  ) {
    appKeyPairId = await getFromStorage(
      sessionId,
      SharedPreferences.MEMBER_CREATION_APP_KEY_PAIR_ID,
    );
  } else if (
    await getFromStorage(
      sessionId,
      SharedPreferences.ORG_CREATION_APP_KEY_PAIR_ID,
    )
  ) {
    appKeyPairId = await getFromStorage(
      sessionId,
      SharedPreferences.ORG_CREATION_APP_KEY_PAIR_ID,
    );
  } else if (
    await getFromStorage(sessionId, SharedPreferences.ACCESS_APP_KEY_PAIR_ID)
  ) {
    appKeyPairId = await getFromStorage(
      sessionId,
      SharedPreferences.ACCESS_APP_KEY_PAIR_ID,
    );
  } else {
    console.log("App key pair id not found.");
    if (failureCallback) {
      failureCallback(BayunError.ERROR_AUTHENTICATION_FAILED);
    } else {
      throw BayunError.ERROR_AUTHENTICATION_FAILED;
    }
  }

  return String(appKeyPairId);
};

/**
 * POST /memberInfo. appKeyPairId may be a single id or comma-separated ids
 * (e.g. accessId,memberCreationId when login + autoCreateMember).
 */
export const getMemberInfoRequest = async (
  appKeyPairId,
  appId,
  orgName,
  orgMemberId,
  email,
  isRegister,
  isAutoCreateEnabled,
) => {
  let memberInfoResponse;

  const restClient = getNonAuthRestClient(appId, appKeyPairId);

  memberInfoResponse = await restClient.post("/memberInfo", {
    orgName,
    orgMemberId,
    emailAddress: email,
    isRegister,
    isAutoCreateEnabled,
  });

  await logApiResponse("memberInfoResponse", memberInfoResponse);
  return memberInfoResponse;
};

/**
 * Get Member information values.
 *
 * @param {String} sessionId id of the session in question.
 * @param {String} appId It is to be provided by the developer for all API calls to LMS.
 * @param {String} appSecret It is to be provided by the developer for all API calls to LMS.
 * @param {String} orgName The name of the org for the authenticating member.
 * @param {String} orgMemberId The memberId of the member authenticating.
 * @param {String} passcode The passcode for the member account.
 * @param {Boolean} autoCreateMember Boolean flag that tells the SDK to create an member
 *                                    with the given credentials if an member doesnt exist for those.
 * @param {Function} successCallback this callback will be run once the authentication flow for
 *                                   the member is finished successfully.
 * @param {Function} failureCallback this callback will be run if the authentication fails.
 */
export const getMemberInfo = async (
  sessionId,
  bayunCore,
  appId,
  appSecret,
  enableFaceRecognition,
  orgName,
  orgMemberId,
  passcode,
  email,
  autoCreateMember,
  loginFlow,
  isOrgOwnedEmail,
  newUserCredentialsCallback,
  securityQuestionsCallback,
  passphraseCallback,
  authorizeMemberCallback,
  successCallback,
  failureCallback,
) => {
  try {
    let isSSO = false;

    if (!passcode) {
      isSSO = true;
    }
    let memberInfoResponse;
    let memberAlreadyExists = false;
    var isAppCreatedWithPasscode = false;

    if (passcode && loginFlow === Constants.LOGIN_FLOW_REGISTER) {
      isAppCreatedWithPasscode = true;
      await saveInStorage(
        sessionId,
        SharedPreferences.MFA_SETTING,
        Constants.MULTI_FACTOR_SINGLE_FA_WITH_PASSCODE,
      );
    }

    let isRegister = true;
    if (loginFlow === Constants.LOGIN_FLOW_LOGIN) {
      isRegister = false;
    } else {
      await saveInStorage(
        sessionId,
        SharedPreferences.IS_REGISTER_FLOW,
        true,
      );
    }

    //find the app key pair id
    var appKeyPairId = await getAppKeyPairId(sessionId, failureCallback);

    const authorizationAppKeyPairId = await getFromStorage(
      sessionId,
      SharedPreferences.AUTHORISATION_APP_KEY_PAIR_ID,
    );

    const memberCreationAppKeyPairId =
      (await getFromStorage(
        sessionId,
        SharedPreferences.MEMBER_CREATION_APP_KEY_PAIR_ID,
      ))

    const accessAppKeyPairId = await getFromStorage(
      sessionId,
      SharedPreferences.ACCESS_APP_KEY_PAIR_ID,
    );

    // /memberInfo app_key_pair_id: single id, or comma-separated Access+MemberCreation when
    // login + autoCreateMember (server resolves roles from the set).
    let memberInfoAppKeyPairId;
    if (isRegister) {
      memberInfoAppKeyPairId = memberCreationAppKeyPairId;
      if (!memberInfoAppKeyPairId) {
        failureCallback(BayunError.ERROR_MEMBER_CREATION_APP_KEY_PAIR_ID_REQUIRED);
        return;
      }
    } else if (autoCreateMember) {
      if (!accessAppKeyPairId) {
        failureCallback(BayunError.ERROR_ACCESS_APP_KEY_PAIR_ID_REQUIRED);
        return;
      }
      if (!memberCreationAppKeyPairId) {
        failureCallback(BayunError.ERROR_MEMBER_CREATION_APP_KEY_PAIR_ID_REQUIRED);
        return;
      }
      memberInfoAppKeyPairId =
        String(accessAppKeyPairId) + "," + String(memberCreationAppKeyPairId);
    } else {
      if (!accessAppKeyPairId) {
        failureCallback(BayunError.ERROR_ACCESS_APP_KEY_PAIR_ID_REQUIRED);
        return;
      }
      memberInfoAppKeyPairId = accessAppKeyPairId;
    }

    memberInfoResponse = await getMemberInfoRequest(
      memberInfoAppKeyPairId,
      appId,
      orgName,
      orgMemberId,
      email,
      isRegister,
      autoCreateMember,
    );

    await saveInStorage(
      sessionId,
      SharedPreferences.ORG_MEMBER_ID,
      orgMemberId,
    );

    if (
      passcode &&
      memberInfoResponse.data.memberAppStatus ==
      Constants.MEMBER_APP_STATUS_NOT_EXISTS
    ) {
      isAppCreatedWithPasscode = true;
    }
    var multiFactorAuthentication =
      memberInfoResponse.data.multiFactorAuthentication;

    if (
      memberInfoResponse.data.isAppCreatedWithPasscode &&
      memberInfoResponse.data.isAppCreatedWithPasscode == "true"
    ) {
      isAppCreatedWithPasscode = true;
    }

    if (multiFactorAuthentication) {
      await saveInStorage(
        sessionId,
        SharedPreferences.MFA_SETTING,
        memberInfoResponse.data.multiFactorAuthentication,
      );
    }

    if (
      !passcode &&
      loginFlow === Constants.LOGIN_FLOW_LOGIN &&
      isAppCreatedWithPasscode &&
      (multiFactorAuthentication ===
        Constants.MULTI_FACTOR_SINGLE_FA_WITH_PASSCODE ||
        multiFactorAuthentication ===
        Constants.MULTI_FACTOR_TWO_FACTOR_AUTHENTICATION_WITHOUT_PASSPHRASE ||
        multiFactorAuthentication ===
        Constants.MULTI_FACTOR_TWO_FACTOR_AUTHENTICATION_WITH_PASSPHRASE)
    ) {
      if (memberInfoResponse.data.userStatus === "NotRegistered") {
        failureCallback(BayunError.ERROR_USER_IS_NOT_REGISTERED);
      } else {
        failureCallback(BayunError.ERROR_USER_PASSCODE_VERIFICATION_ENABLED);
      }
      return;
    }

    if (
      (!isAppCreatedWithPasscode &&
        memberInfoResponse.data.memberAppStatus !=
        Constants.MEMBER_APP_STATUS_NOT_EXISTS) ||
      (memberInfoResponse.data.memberAppStatus !=
        Constants.MEMBER_APP_STATUS_NOT_EXISTS &&
        (memberInfoResponse.data.multiFactorAuthentication ===
          Constants.MULTI_FACTOR_SINGLE_FA_WITH_SECURITY_QUESTIONS ||
          memberInfoResponse.data.multiFactorAuthentication ===
          Constants.MULTI_FACTOR_SINGLE_FA_WITH_SECURITY_QUESTIONS_AND_PASSPHRASE))
    ) {
      isSSO = true;
    }

    if (
      !passcode &&
      memberInfoResponse.data.memberAppStatus ===
      Constants.MEMBER_APP_STATUS_NOT_EXISTS
    ) {
      isSSO = true;
    }
    //Error handling -->
    // Register flow
    if (loginFlow == Constants.LOGIN_FLOW_REGISTER) {
      if (
        isSSO &&
        memberInfoResponse.data.memberAppStatus ===
        Constants.MEMBER_APP_STATUS_LINKED &&
        memberInfoResponse.data.memberId
      ) {
        if (memberInfoResponse.data.isAppCreatedWithPasscode) {
          failureCallback(BayunError.ERROR_MEMBER_ALREADY_EXISTS);
          return;
        } else {
          failureCallback(BayunError.ERROR_USER_ALREADY_EXISTS);
          return;
        }
      }
      if (
        !isSSO &&
        memberInfoResponse.data.memberAppStatus ===
        Constants.MEMBER_APP_STATUS_LINKED &&
        memberInfoResponse.data.memberId
      ) {
        failureCallback(BayunError.ERROR_MEMBER_ALREADY_EXISTS);
        return;
      }
      // if (memberInfoResponse.data.memberId && !memberInfoResponse.data.memberAppId && !memberInfoResponse.data.userId) {
      //   failureCallback(BayunError.ERROR_MEMBER_ALREADY_EXISTS_FOR_A_DIFFERENT_EMAIL_ADDRESS);
      //   return;
      // }
    } else {
      if (memberInfoResponse.data.memberId == null && !autoCreateMember) {
        failureCallback(BayunError.ERROR_MEMBER_DOESNT_EXIST);
        return;
      }
      // if (
      //   memberInfoResponse.data.memberAppStatus ===
      //   Constants.MEMBER_APP_STATUS_NOT_EXISTS &&
      //   autoCreateMember
      // ) { // todo
      //   failureCallback(BayunError.ERROR_APP_NOT_LINKED);
      //   return;
      // }
      if (
        memberInfoResponse.data.memberAppStatus ===
        Constants.MEMBER_APP_STATUS_NOT_EXISTS && !autoCreateMember
      ) {
        failureCallback(BayunError.ERROR_MEMBER_APP_NOT_REGISTERED);
        return;
      }
      if (memberInfoResponse.data.memberId == null) {
        isSSO = false;
      }
    }

    const emailId = memberInfoResponse.data.emailId || email;
    if (emailId) {
      await saveInStorage(sessionId, SharedPreferences.EMAIL_ADDRESS, emailId);
    }

    if (isSSO) {
      await saveInStorage(
        sessionId,
        SharedPreferences.APP_KEY_PAIR_ID,
        appKeyPairId,
      );

      await saveInStorage(sessionId, SharedPreferences.ORG_NAME, orgName);
    }

    //Choose AppKeyPairId based on privilege priority
    var appPrivateKey = "";

    if (
      await getFromStorage(
        sessionId,
        SharedPreferences.ORG_CREATION_APP_KEY_PAIR_ID,
      )
    ) {
      appPrivateKey = await getFromInternalStorage(
        sessionId,
        SharedPreferences.ORG_CREATION_APP_PRIVATE_KEY,
      );
    }
    else if (
      await getFromStorage(
        sessionId,
        SharedPreferences.MEMBER_CREATION_APP_KEY_PAIR_ID,
      )
    ) {
      appPrivateKey = await getFromInternalStorage(
        sessionId,
        SharedPreferences.MEMBER_CREATION_APP_PRIVATE_KEY,
      );
    }
    else if (
      await getFromStorage(
        sessionId,
        SharedPreferences.AUTHORISATION_APP_KEY_PAIR_ID,
      )
    ) {
      appPrivateKey = await getFromInternalStorage(
        sessionId,
        SharedPreferences.AUTHORISATION_APP_PRIVATE_KEY,
      );
    } else if (
      await getFromStorage(sessionId, SharedPreferences.ACCESS_APP_KEY_PAIR_ID)
    ) {
      appPrivateKey = await getFromInternalStorage(
        sessionId,
        SharedPreferences.ACCESS_APP_PRIVATE_KEY,
      );
    } else {
      console.log("App key pair id not found.");
      failureCallback(BayunError.ERROR_AUTHENTICATION_FAILED);
    }

    // verify adminPublicKey
    if (memberInfoResponse.data.adminPublicKey) {
      if (
        !(await retrieveAndverifyLastSignature(
          sessionId,
          memberInfoResponse.data.adminPublicKey,
        ))
      ) {
        console.log("adminPublicKey verification failed.");
        failureCallback(BayunError.ERROR_MEMBER_VERIFICATION_FAILED);
      } else {
        await saveInStorage(
          sessionId,
          SharedPreferences.ADMIN_PUBLIC_KEY,
          getMessageFromSignatureString(memberInfoResponse.data.adminPublicKey),
        );
      }
    }

    // verify backdoorPublicKey
    if (memberInfoResponse.data.backdoorPublicKey) {
      if (
        !(await retrieveAndverifyLastSignature(
          sessionId,
          memberInfoResponse.data.backdoorPublicKey,
        ))
      ) {
        console.log("backdoorPublicKey verification failed.");
        failureCallback(BayunError.ERROR_MEMBER_VERIFICATION_FAILED);
      } else {
        await saveInStorage(
          sessionId,
          SharedPreferences.BACKDOOR_PUBLIC_KEY,
          getMessageFromSignatureString(memberInfoResponse.data.backdoorPublicKey),
        );
      }
    }

    // verify ghostMemberId
    if (memberInfoResponse.data.ghostMemberId) {
      if (
        !(await verifyMessageForPublicKeyTag(
          sessionId,
          memberInfoResponse.data.ghostMemberId,
          "",
          SigningPublicKeyTags.BAYUN_SERVER_PUBLIC_KEY_TAG,
        ))
      ) {
        console.log("ghostMemberId verification failed.");
        failureCallback(BayunError.ERROR_MEMBER_VERIFICATION_FAILED);
        return;
      } else {
        await saveInStorage(
          sessionId,
          SharedPreferences.GHOST_MEMBER_ID,
          memberInfoResponse.data.ghostMemberId,
        );
      }
    }

    // verify ghostMemberPublicKey
    if (memberInfoResponse.data.ghostMemberPublicKey) {
      if (
        !(await verifyMessageForPublicKeyTag(
          sessionId,
          memberInfoResponse.data.ghostMemberPublicKey,
          "",
          SigningPublicKeyTags.BAYUN_SERVER_PUBLIC_KEY_TAG,
        ))
      ) {
        console.log("ghostMemberPublicKey verification failed.");
        failureCallback(BayunError.ERROR_MEMBER_VERIFICATION_FAILED);
      } else {
        await saveInStorage(
          sessionId,
          SharedPreferences.GHOST_MEMBER_SIGNING_PUBLIC_KEY,
          getMessageFromSignatureString(
            memberInfoResponse.data.ghostMemberPublicKey,
          ),
        );
        await saveInStorage(
          sessionId,
          SharedPreferences.GHOST_MEMBER_SIGNING_PUBLIC_KEY_WITH_SIGNATURES,
          memberInfoResponse.data.ghostMemberPublicKey,
        );
      }
    }

    // An admin-approved signedAppId is signed with the admin signing key, so verifying it needs the
    // admin signing public key. Login receives that on the lockbox response; registration receives
    // it here. Verified against the server signature chain before it is trusted.
    if (memberInfoResponse.data.adminSigningPublicKey) {
      try {
        await prepareLockBoxVerificationKeys(sessionId, {
          adminSigningPublicKey: memberInfoResponse.data.adminSigningPublicKey,
        });
      } catch (error) {
        failureCallback(BayunError.ERROR_MEMBER_VERIFICATION_FAILED);
        return;
      }
    }

    if (
      memberInfoResponse.data.signedAppId &&
      !(await verifyOrgAppApprovalSignature(
        sessionId,
        memberInfoResponse.data.signedAppId,
        appId,
        orgName,
      ))
    ) {
      console.log("signedAppId verification failed.");
      failureCallback(BayunError.ERROR_MEMBER_VERIFICATION_FAILED);
      return;
    }

    if (memberInfoResponse.data.userId) {
      // Verify and save userId
      if (
        !(await retrieveAndverifyLastSignature(
          sessionId,
          memberInfoResponse.data.userId,
        ))
      ) {
        console.log("userId verification failed.");
        failureCallback(BayunError.ERROR_MEMBER_VERIFICATION_FAILED);
        return;
      } else {
        await saveInStorage(
          sessionId,
          SharedPreferences.USER_ID,
          getMessageFromSignatureString(memberInfoResponse.data.userId),
        );
      }
    }

    if (memberInfoResponse.data.orgId) {
      //Verify and save orgId
      if (
        !(await retrieveAndverifyLastSignature(
          sessionId,
          memberInfoResponse.data.orgId,
        ))
      ) {
        console.log("orgId verification failed.");
        failureCallback(BayunError.ERROR_MEMBER_VERIFICATION_FAILED);
      } else {
        await saveInStorage(
          sessionId,
          SharedPreferences.ORG_ID,
          getMessageFromSignatureString(memberInfoResponse.data.orgId),
        );
      }
      if (memberInfoResponse.data.memberId) {
        //Verify and save memberId
        if (
          !(await retrieveAndverifyLastSignature(
            sessionId,
            memberInfoResponse.data.memberId,
          ))
        ) {
          console.log("memberId verification failed.");
          failureCallback(BayunError.ERROR_MEMBER_VERIFICATION_FAILED);
          return;
        } else {
          await saveInStorage(
            sessionId,
            SharedPreferences.MEMBER_ID,
            getMessageFromSignatureString(memberInfoResponse.data.memberId),
          );
        }
      }

      if (memberInfoResponse.data.memberAppId) {
        //Verify and save memberAppId
        if (
          !(await retrieveAndverifyLastSignature(
            sessionId,
            memberInfoResponse.data.memberAppId,
          ))
        ) {
          console.log("memberAppId verification failed.");
          failureCallback(BayunError.ERROR_MEMBER_VERIFICATION_FAILED);
        } else {
          await saveInStorage(
            sessionId,
            SharedPreferences.MEMBER_APP_ID,
            getMessageFromSignatureString(memberInfoResponse.data.memberAppId),
          );
        }
      }
    }

    if (memberInfoResponse.data.isOrgAppApproved == "false") {
      failureCallback(
        BayunError.ERROR_REGISTRATION_FAILED_APPLICATION_IS_NOT_APPROVED,
      );
      return;
    }

    if (!isSSO && passcode && memberInfoResponse.data.memberKeySalt) {
      const authPasscodeKey = await derivePbkdf2Encoded(
        passcode,
        memberInfoResponse.data.memberKeySalt,
      );

      await saveInStorage(
        sessionId,
        SharedPreferences.AUTH_PASSCODE_KEY,
        authPasscodeKey,
      );
    }

    // save the appId
    await saveInStorage(sessionId, SharedPreferences.APP_ID, appId);

    if (
      loginFlow === Constants.LOGIN_FLOW_REGISTER &&
      memberInfoResponse.data.memberAppStatus ==
      Constants.MEMBER_APP_STATUS_NOT_EXISTS
    ) {
    }

    await saveInStorage(
      sessionId,
      SharedPreferences.ENABLE_FACE_RECOGNITION,
      enableFaceRecognition,
    );

    if (memberInfoResponse.data.memberAppStatus) {
      await saveInStorage(
        sessionId,
        SharedPreferences.MEMBER_APP_STATUS,
        memberInfoResponse.data.memberAppStatus,
      );
    }

    if (isSSO) {
      let isCreateOrgRequest = false;
      if (!memberInfoResponse.data.orgId) {
        isCreateOrgRequest = true;
      }

      await saveInStorage(
        sessionId,
        SharedPreferences.IS_CREATE_ORG_REQUEST,
        isCreateOrgRequest,
      );

      await saveInInternalStorage(sessionId, SharedPreferences.APP_SECRET, appSecret);

      await getMemberInfoForSSO(
        sessionId,
        bayunCore,
        orgMemberId,
        orgName,
        passcode,
        memberInfoResponse.data.memberKeySalt,
        email,
        isOrgOwnedEmail,
        memberInfoResponse.data.userStatus,
        appId,
        appKeyPairId,
        appSecret,
        loginFlow,
        memberInfoResponse.data.memberAppStatus,
        memberInfoResponse.data.multiFactorAuthentication,
        isAppCreatedWithPasscode,
        securityQuestionsCallback,
        passphraseCallback,
        authorizeMemberCallback,
        newUserCredentialsCallback,
        successCallback,
        failureCallback,
      );
    } else {
      // check if org and member exist already

      if (memberInfoResponse.data.memberId) {
        //if member exist in register flow but app does not exist then create new app
        if (
          loginFlow === Constants.LOGIN_FLOW_REGISTER || autoCreateMember &&
          memberInfoResponse.data.memberAppStatus ==
          Constants.MEMBER_APP_STATUS_NOT_EXISTS
        ) {
          await createMemberAppKeyPair(
            sessionId,
            orgName,
            orgMemberId,
            passcode,
            memberInfoResponse.data.memberAuthSalt,
            memberInfoResponse.data.memberKeySalt,
            appId,
            appSecret,
            appKeyPairId,
          );

          failureCallback(BayunError.ERROR_APP_NOT_LINKED);

          return;
        }

        // save the member auth salt
        await saveInStorage(
          sessionId,
          SharedPreferences.MEMBER_AUTH_SALT,
          memberInfoResponse.data.memberAuthSalt,
        );
        // member exists, and is to be authenticated
        if (
          memberInfoResponse.data.memberAppStatus ===
          Constants.MEMBER_APP_STATUS_LINKED
        ) {
          memberAlreadyExists = true;
          // authenticate the member with the given passcode

          if (!await validatePasscode(
            sessionId,
            appId,
            appSecret,
            accessAppKeyPairId,
            orgName,
            orgMemberId,
            passcode,
            failureCallback,
          )
          ) {
            return;
          }

          // If 2FA token is returned then the member has 2FA enabled
          if (email) {
            await getTwoFAData(
              sessionId,
              bayunCore,
              appId,
              appSecret,
              passcode,
              successCallback,
              failureCallback,
              securityQuestionsCallback,
              passphraseCallback,
              authorizeMemberCallback,
            );
          } else {
            await saveInStorage(
              sessionId,
              SharedPreferences.IS_TWO_FA_ENABLED,
              false,
            );

            await getMemberClientData(
              sessionId,
              orgMemberId,
              orgName,
              passcode,
              failureCallback
            );

            await saveInStorage(
              sessionId,
              SharedPreferences.LAST_LOGIN_TIME,
              Date.now(),
            );

            successCallback({
              sessionId,
              securityQuestions: null,
              authenticationResponse: AuthenticateResponse.SUCCESS,
              memberAlreadyExists,
            });
          }
        } else if (
          memberInfoResponse.data.memberAppStatus ===
          Constants.MEMBER_APP_STATUS_UNLINKED
        ) {
          // the member app is already created but the linking needs to be done from admin
          failureCallback(BayunError.ERROR_APP_NOT_LINKED);
        } else if (
          memberInfoResponse.data.memberAppStatus ===
          Constants.MEMBER_APP_STATUS_NOT_EXISTS
        ) {
          // UPDATE ERROR TO APP NOT EXIST
          console.log("Error in login for new app");
          failureCallback(BayunError.ERROR_MEMBER_DOESNT_EXIST);
          return;
        }
      } else if (autoCreateMember) {
        // save the orgName and orgMemberId
        await saveInStorage(sessionId, SharedPreferences.ORG_NAME, orgName);
        await saveInStorage(
          sessionId,
          SharedPreferences.ORG_MEMBER_ID,
          orgMemberId,
        );

        const memberRegistrationComplete = !memberInfoResponse.data.orgId
          ? await createMemberData(
            sessionId,
            orgName,
            orgMemberId,
            passcode,
            appId,
            appSecret,
            appKeyPairId,
            memberInfoResponse.data.ghostMemberId,
            memberInfoResponse.data.ghostMemberPublicKey,
            memberInfoResponse.data.adminPublicKey,
            true,
            email,
            null,
            authorizeMemberCallback,
            failureCallback,
          )
          : await createMemberData(
            sessionId,
            orgName,
            orgMemberId,
            passcode,
            appId,
            appSecret,
            appKeyPairId,
            memberInfoResponse.data.ghostMemberId,
            memberInfoResponse.data.ghostMemberPublicKey,
            memberInfoResponse.data.adminPublicKey,
            false,
            email,
            null,
            authorizeMemberCallback,
            failureCallback,
          );

        // createMemberData already invoked failureCallback (e.g. authorization
        // pending when the secret has no authorization key pair). Do not remap
        // that to MEMBER_NOT_APPROVED_YET — passphrase register does the same.
        if (!memberRegistrationComplete) {
          return;
        }

        await saveInStorage(
          sessionId,
          SharedPreferences.LAST_LOGIN_TIME,
          Date.now(),
        );

        successCallback({
          sessionId,
          securityQuestions: null,
          authenticationResponse: AuthenticateResponse.SUCCESS,
          memberAlreadyExists,
        });
      } else {
        failureCallback(BayunError.ERROR_MEMBER_DOESNT_EXIST);
      }
    }
  } catch (error) {
    // getMemberClientData (and similar) already invoke failureCallback and rethrow
    // with bayunAlreadyHandled. Re-handling here would remap a plain Error to
    // ERROR_SOMETHING_WENT_WRONG (JSON.stringify(Error) === "{}").
    if (error?.bayunAlreadyHandled) {
      return;
    }
    console.log(error);
    handleError(sessionId, error, failureCallback, "getMemberInfo");
    return;
  }
};

/**
 * This method calls the API to get the application keys.
 *
 * @param {String} sessionId id of the session in question.
 * @param {String} appId It is to be provided by the developer for all API calls to LMS.
 * @param {String} appSecret It is to be provided by the developer for all API calls to LMS.
 */
export const getApplicationKeyPair = async (
  sessionId,
  appId,
  appSecret,
  orgMemberId,
  bayunServerPublicKey,
) => {

  // create a rest client
  // sessionId so the account key returned by /applicationKeyPairs is recorded: this is the call
  // that establishes the session, and the only response carrying that key.
  const restClient = getTwoFARestClient(appId, sessionId);

  const appSecretHash = await generateHMacHash(appSecret, appId);

  // call getApplicationKeyPair API
  let serverVerificationSalt = generateBCryptSalt();
  let getApplicationKeyPairResponse = await restClient.post(
    "/applicationKeyPairs",
    {
      bayunServerPublicKey,
      requestSource: `${process.env.SOURCE}`,
      serverVerificationSalt,
      appSecretHash,
    },
    {
      headers: {
        requestSource: `${process.env.SOURCE}`,
      },
    },
  );

  await logApiResponse(
    "getApplicationKeyPairResponse",
    getApplicationKeyPairResponse,
  );

  await saveInStorage(
    sessionId,
    SharedPreferences.BAYUN_SERVER_PUBLIC_KEY,
    bayunServerPublicKey,
  );
  await saveEccCurvesFromResponse(sessionId, getApplicationKeyPairResponse.data);
  //Verify signedServerVerificationSalt (serverPrivateKey)
  if (
    !(await retrieveAndverifyLastSignature(
      sessionId,
      getApplicationKeyPairResponse.data.signedServerVerificationSalt,
    ))
  ) {
    console.log("signedServerVerificationSalt verfication failed");
    throw BayunError.ERROR_SERVER_VERIFICATION_FAILED;
  }

  // save appSecretSalt
  await saveInStorage(
    sessionId,
    SharedPreferences.APP_SECRET_SALT,
    getApplicationKeyPairResponse.data.appSecretSalt,
  );

  // save the data
  await saveInStorage(
    sessionId,
    SharedPreferences.ENCRYPTION_TYPE,
    getApplicationKeyPairResponse.data.encryptionType,
  );

  await saveInStorage(sessionId, SharedPreferences.ORG_MEMBER_ID, orgMemberId);

  // create appPrivKeyDecryptionKey
  const appPrivKeyDecryptionKey = await derivePbkdf2Raw(
    appSecret,
    getApplicationKeyPairResponse.data.appSecretSalt,
  );

  for (const appKeyPair of getApplicationKeyPairResponse.data.appKeyPairs) {
    if (appKeyPair.role === Constants.APP_KEY_PAIR_ROLE_ACCESS) {
      // decrypt Access App Private Key
      const appPrivateKey = await aeadDecryptWithAssociatedData(
        appKeyPair.appPrivateKey,
        appId,
        appPrivKeyDecryptionKey,
      );

      if (!appPrivateKey) {
        console.log("Access App Private Key Decryption Failed");
        throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
      }

      // save accessAppPrivateKey and accessAppPrivateKeyPairId
      await saveInStorage(
        sessionId,
        SharedPreferences.ACCESS_APP_KEY_PAIR_ID,
        appKeyPair.appKeyPairId,
      );

      await saveInInternalStorage(
        sessionId,
        SharedPreferences.ACCESS_APP_PRIVATE_KEY,
        appPrivateKey,
      );
      await decryptAndStoreAppSigningKey(
        sessionId,
        appKeyPair,
        appId,
        appPrivKeyDecryptionKey,
        SharedPreferences.ACCESS_APP_SIGNING_PRIVATE_KEY,
        SharedPreferences.ACCESS_APP_SIGNING_PUBLIC_KEY,
      );
    }
    else if (appKeyPair.role === Constants.APP_KEY_PAIR_ROLE_MEMBER_CREATION) {

      const appPrivateKey = await aeadDecryptWithAssociatedData(
        appKeyPair.appPrivateKey,
        appId,
        appPrivKeyDecryptionKey,
      );

      if (!appPrivateKey) {
        console.log(`${appKeyPair.role} App Private Key Decryption Failed`);
        throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
      }

      await saveInStorage(
        sessionId,
        SharedPreferences.MEMBER_CREATION_APP_KEY_PAIR_ID,
        appKeyPair.appKeyPairId,
      );
      await saveInInternalStorage(
        sessionId,
        SharedPreferences.MEMBER_CREATION_APP_PRIVATE_KEY,
        appPrivateKey,
      );
      await saveInStorage(
        sessionId,
        SharedPreferences.MEMBER_CREATION_APP_PUBLIC_KEY,
        appKeyPair.appPublicKey,
      );
      await decryptAndStoreAppSigningKey(
        sessionId,
        appKeyPair,
        appId,
        appPrivKeyDecryptionKey,
        SharedPreferences.MEMBER_CREATION_APP_SIGNING_PRIVATE_KEY,
        SharedPreferences.MEMBER_CREATION_APP_SIGNING_PUBLIC_KEY,
      );

    }
    else if (appKeyPair.role === Constants.APP_KEY_PAIR_ROLE_ORG_CREATION) {

      const appPrivateKey = await aeadDecryptWithAssociatedData(
        appKeyPair.appPrivateKey,
        appId,
        appPrivKeyDecryptionKey,
      );

      if (!appPrivateKey) {
        console.log(`${appKeyPair.role} App Private Key Decryption Failed`);
        throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
      }

      await saveInStorage(
        sessionId,
        SharedPreferences.ORG_CREATION_APP_KEY_PAIR_ID,
        appKeyPair.appKeyPairId,
      );
      await saveInInternalStorage(
        sessionId,
        SharedPreferences.ORG_CREATION_APP_PRIVATE_KEY,
        appPrivateKey,
      );
      await decryptAndStoreAppSigningKey(
        sessionId,
        appKeyPair,
        appId,
        appPrivKeyDecryptionKey,
        SharedPreferences.ORG_CREATION_APP_SIGNING_PRIVATE_KEY,
        SharedPreferences.ORG_CREATION_APP_SIGNING_PUBLIC_KEY,
      );
      await saveInStorage(
        sessionId,
        SharedPreferences.ORG_CREATION_APP_PUBLIC_KEY,
        appKeyPair.appPublicKey,
      );
      await saveInInternalStorage(
        sessionId,
        SharedPreferences.ORG_CREATION_APP_PRIVATE_KEY,
        appPrivateKey,
      );
    }
    else if (appKeyPair.role === Constants.APP_KEY_PAIR_ROLE_AUTHORISATION) {
      //Decrypt Access App Private Key
      const appPrivateKey = await aeadDecryptWithAssociatedData(
        appKeyPair.appPrivateKey,
        appId,
        appPrivKeyDecryptionKey,
      );

      if (!appPrivateKey) {
        console.log("Authorization App Private Key Decryption Failed");
        throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
      }

      // save accessAppPrivateKey and accessAppPrivateKeyPairId
      await saveInStorage(
        sessionId,
        SharedPreferences.AUTHORISATION_APP_KEY_PAIR_ID,
        appKeyPair.appKeyPairId,
      );

      await saveInInternalStorage(
        sessionId,
        SharedPreferences.AUTHORISATION_APP_PRIVATE_KEY,
        appPrivateKey,
      );
      await decryptAndStoreAppSigningKey(
        sessionId,
        appKeyPair,
        appId,
        appPrivKeyDecryptionKey,
        SharedPreferences.AUTHORISATION_APP_SIGNING_PRIVATE_KEY,
        SharedPreferences.AUTHORISATION_APP_SIGNING_PUBLIC_KEY,
      );
    }
  }
};

/**
 * Authenticate an existing member with the passcode.
 *
 * @param {String} sessionId id of the session in question.
 * @param {String} appId It is to be provided by the developer for all API calls to LMS.
 * @param {String} appSecret It is to be provided by the developer for all API calls to LMS.
 * @param {String} orgName The name of the org for the authenticating member.
 * @param {String} orgMemberId The memberId of the member authenticating.
 * @param {String} passcode The passcode for the member account.
 */
export const validatePasscode = async (
  sessionId,
  appId,
  appSecret,
  appKeyPairId,
  orgName,
  orgMemberId,
  passcode,
  failureCallback,
) => {
  // create a rest client
  // The token refresh that follows /validatePasscode runs on this same client, and that
  // refresh resolves a session — so it needs the account key.
  const restClient = getNonAuthRestClient(appId, appKeyPairId, sessionId);

  try {
    // create the authPasscodeHash
    const authPasscodeHash = await derivePbkdf2Encoded(
      passcode,
      await getFromStorage(sessionId, SharedPreferences.MEMBER_AUTH_SALT),
    );

    const memberAppId = await getFromStorage(
      sessionId,
      SharedPreferences.MEMBER_APP_ID,
    );
    const userId = await getFromStorage(sessionId, SharedPreferences.USER_ID);

    // call the /validatePasscode API
    const validatePasscodeResponse = await restClient.post(
      "/validatePasscode",
      {
        authPasscodeHash,
        memberAppId,
        userId,
      },
    );
    // console.log(/validatePasscodeResponse);
    await logApiResponse("validatePasscodeResponse", validatePasscodeResponse);

    // save the data
    await saveInStorage(sessionId, SharedPreferences.ORG_NAME, orgName);
    await saveInStorage(
      sessionId,
      SharedPreferences.ORG_MEMBER_ID,
      orgMemberId,
    );
    await getRefreshToken(validatePasscodeResponse, restClient);

    await getStorageEncryptionKey(sessionId);
    await saveInStorage(sessionId, SharedPreferences.APP_ID, appId);
    await saveInStorage(sessionId, SharedPreferences.APP_SECRET, appSecret);
    return true;
  } catch (error) {
    handleError(sessionId, error, failureCallback, "validatePasscode");
  }
};

/**
 * Gets the member client information for an member.
 *
 * @param {String} sessionId id of the session in question.
 * @param {String} orgMemberId  The memberId of the member authenticating.
 * @param {String} passcode The passcode for the member account.
 */
export const getMemberClientData = async (
  sessionId,
  orgMemberId,
  orgName,
  passcode,
  failureCallback,
) => {
  // create a rest client
  let authRestClient = await getAuthRestclient(sessionId);

  try {
    // call the memberClientData API
    const getMemberClientDataResponse =
      await authRestClient.get("/memberClientData");

    // console.log(getMemberClientDataResponse);
    await logApiResponse(
      "getMemberClientDataResponse",
      getMemberClientDataResponse,
    );

    // verify and save orgId
    if (getMemberClientDataResponse.data.orgId) {
      if (
        !(await retrieveAndverifyLastSignature(
          sessionId,
          getMemberClientDataResponse.data.orgId,
        ))
      ) {
        console.log("orgId verification failed.");
        throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
      } else {
        await saveInStorage(
          sessionId,
          SharedPreferences.ORG_ID,
          getMessageFromSignatureString(getMemberClientDataResponse.data.orgId),
        );
      }
    }

    // verify and save memberId
    if (getMemberClientDataResponse.data.memberId) {
      if (
        !(await retrieveAndverifyLastSignature(
          sessionId,
          getMemberClientDataResponse.data.memberId,
        ))
      ) {
        console.log("memberId verification failed.");
        throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
      } else {
        await saveInStorage(
          sessionId,
          SharedPreferences.MEMBER_ID,
          getMessageFromSignatureString(
            getMemberClientDataResponse.data.memberId,
          ),
        );
      }
    }

    if (getMemberClientDataResponse.data.memberAppId) {
      //Verify and save memberAppId
      if (
        !(await retrieveAndverifyLastSignature(
          sessionId,
          getMemberClientDataResponse.data.memberAppId,
        ))
      ) {
        console.log("memberAppId verification failed.");
        throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
      } else {
        await saveInStorage(
          sessionId,
          SharedPreferences.MEMBER_APP_ID,
          getMessageFromSignatureString(
            getMemberClientDataResponse.data.memberAppId,
          ),
        );
      }
    }

    if (
      (passcode && getMemberClientDataResponse.data.memberAppPrivateKey) ||
      ((await getFromStorage(sessionId, SharedPreferences.AUTH_PASSCODE_KEY)) &&
        getMemberClientDataResponse.data.memberAppPrivateKey)
    ) {
      // decrypt and save the memberAppPrivateKey
      const decryptMemberAppPrivateKey = await aeadDecryptWithPasscode(
        getMemberClientDataResponse.data.memberAppPrivateKey,
        passcode,
        getMemberClientDataResponse.data.memberKeySalt,
        orgMemberId,
        sessionId,
      );
      if (!decryptMemberAppPrivateKey) {
        console.log("decryptMemberAppPrivateKey decryption failed.");
        throw BayunError.ERROR_DECRYPTION_FAILED;
      }
      await saveInStorage(
        sessionId,
        SharedPreferences.MEMBER_APP_PRIVATE_KEY,
        decryptMemberAppPrivateKey,
      );
      await saveInStorage(
        sessionId,
        SharedPreferences.MEMBER_KEY_SALT,
        getMemberClientDataResponse.data.memberKeySalt,
      );
    }

    const memberStatus = getMemberClientDataResponse.data.memberStatus;
    await saveInStorage(
      sessionId,
      SharedPreferences.MEMBER_STATUS,
      memberStatus,
    );

    if (memberStatus != "Cancelled" && memberStatus != "Registered") {
      // call the memberLockbox API
      await getMemberLockBox(sessionId, orgMemberId);
      await decryptAndSaveMemberInfo(
        sessionId,
        orgName,
        getMemberClientDataResponse.data,
      );

      // save device info
      await saveDeviceInfo(sessionId);
    } else {
      throw BayunError.ERROR_USER_INACTIVE;
    }
  } catch (error) {
    handleError(sessionId, error, failureCallback, "getMemberClientData");
    const handledError = new Error(
      error?.response?.data?.errorMessage || error?.message || String(error),
    );
    handledError.bayunAlreadyHandled = true;
    throw handledError;
  }
};


/**
 * Gets member's lockbox, decrypt and save the decrypted member keys.
 *
 * @param {String} sessionId id of the session in question.
 * @param {String} orgMemberId The memberId of the member authenticating.
 */
export const getMemberLockBox = async (sessionId, orgMemberId) => {
  let authRestClient;
  // let apiEndPoint = "/memberLockBox";

  try {
    const secret = await getFromStorage(
      sessionId,
      SharedPreferences.VISHWAM_SECRET,
    );

    let params = {};

    // if (process.env.SOURCE == Constants.SOURCE.BAYUN_ADMIN) {
    //   authRestClient = await getAuthRestClientForAdmin();
    //   apiEndPoint = "/admin/memberLockBox";

    const memberAppId = await getFromStorage(
      sessionId,
      SharedPreferences.MEMBER_APP_ID,
    );
    //   params = { secret, memberAppId };
    // } else {
    authRestClient = await getAuthRestclient(sessionId);
    params = { secret, memberAppId };
    // }

    const getMemberLockBoxResponse = await authRestClient.get(
      "/memberLockBox",
      {
        params,
      },
    );

    // console.log(getMemberLockBoxResponse);
    await logApiResponse("getMemberLockBoxResponse", getMemberLockBoxResponse);

    const memberStatus = getMemberLockBoxResponse.data.memberStatus;

    // save the member status
    await saveInStorage(
      sessionId,
      SharedPreferences.MEMBER_STATUS,
      memberStatus,
    );

    const keyValidationPolicy = getMemberLockBoxResponse.data.keyValidationPolicy;
    await saveInStorage(
      sessionId,
      SharedPreferences.KEY_VALIDATION_POLICY,
      keyValidationPolicy,
    );

    if (memberStatus != "Cancelled" && memberStatus != "Registered") {
      const lockBoxData = getMemberLockBoxResponse.data;

      let userPrivateKey = await getFromStorage(
        sessionId,
        SharedPreferences.USER_PRIVATE_KEY,
      );

      if (!userPrivateKey) {
        userPrivateKey = await getFromInternalStorage(
          sessionId,
          SharedPreferences.USER_PRIVATE_KEY,
        );
        await saveInStorage(
          sessionId,
          SharedPreferences.USER_PRIVATE_KEY,
          userPrivateKey,
        );
      }

      let memberPrivateKey = lockBoxData.memberPrivateKey;
      const encryptionCurve = await getEncryptionCurveFromStorage(sessionId);

      if (userPrivateKey && lockBoxData.memberPrivateKeyOuter_kek) {
        const userId = await getFromStorage(
          sessionId,
          SharedPreferences.USER_ID,
        );
        memberPrivateKey = await decryptAsymmetric(
          memberPrivateKey,
          userPrivateKey,
          lockBoxData.memberPrivateKeyOuter_kek,
          userId,
          encryptionCurve,
        );
      }

      const memberAppPrivateKey = await getFromStorage(
        sessionId,
        SharedPreferences.MEMBER_APP_PRIVATE_KEY,
      );

      if (memberAppPrivateKey && lockBoxData.memberPrivateKeyInner_kek) {
        memberPrivateKey = await decryptAsymmetric(
          memberPrivateKey,
          memberAppPrivateKey,
          lockBoxData.memberPrivateKeyInner_kek,
          orgMemberId,
          encryptionCurve,
        );
      }

      await prepareLockBoxVerificationKeys(sessionId, lockBoxData);
      await verifyLockBoxResponse(sessionId, lockBoxData, memberPrivateKey);


      const { signedSettings, orgSettingsLastUpdatedAt } =
        getMemberLockBoxResponse.data;

      if (signedSettings && orgSettingsLastUpdatedAt != null) {
        const orgName = await getFromStorage(
          sessionId,
          SharedPreferences.ORG_NAME,
        );
        const adminPublicKey = await getFromStorage(
          sessionId,
          SharedPreferences.ADMIN_PUBLIC_KEY,
        );

        // No `if (adminPublicKey)` gate: a missing key must fail the verification, not skip it.
        await verifyAndPersistOrgSettingsFromLastUpdatedAt(
          signedSettings,
          orgSettingsLastUpdatedAt,
          sessionId,
          adminPublicKey,
          orgName,
          {
            keyValidationPolicy: getMemberLockBoxResponse.data.keyValidationPolicy,
            memberApproval: getMemberLockBoxResponse.data.memberApproval,
            appApproval: getMemberLockBoxResponse.data.appApproval,
          },
        );
      }


      await saveInStorage(
        sessionId,
        SharedPreferences.MEMBER_PRIVATE_KEY,
        memberPrivateKey,
      );
      // decrypt orgkey and save it

      if (lockBoxData.orgKey) {
        const orgkey = asRawKey(
          await decryptAsymmetric(
            lockBoxData.orgKey,
            memberPrivateKey,
            lockBoxData.orgKey_kek,
            orgMemberId,
            encryptionCurve,
          ),
        );
        await saveInStorage(
          sessionId,
          SharedPreferences.ORG_KEY,
          encodeKey(orgkey),
        );
      }
      if (lockBoxData.orgPrivateKey) {
        // decrypt the orgSigningPrivate key and save it
        const orgSigningPrivateKey = await decryptAsymmetric(
          lockBoxData.orgPrivateKey,
          memberPrivateKey,
          lockBoxData.orgPrivateKey_kek,
          orgMemberId,
          encryptionCurve,
        );
        await saveInStorage(
          sessionId,
          SharedPreferences.ORG_SIGNING_PRIVATE_KEY,
          orgSigningPrivateKey,
        );
      }

      // decrypt memberKey and save it
      const memberKey = asRawKey(
        await decryptAsymmetric(
          lockBoxData.memberKey,
          memberPrivateKey,
          lockBoxData.memberKey_kek,
          orgMemberId,
          encryptionCurve,
        ),
      );
      await saveInStorage(
        sessionId,
        SharedPreferences.MEMBER_KEY,
        encodeKey(memberKey),
      );

      // await saveInStorage(
      //   sessionId,
      //   SharedPreferences.ORG_SIGNING_PUBLIC_KEY,
      //   lockBoxData.orgPublicKey
      // );

      // decrypt the memberSigningPrivateKey and save it
      const memberSigningPrivateKey = await decryptAsymmetric(
        lockBoxData.memberSigningPrivateKey,
        memberPrivateKey,
        lockBoxData.memberSigningPrivateKey_kek,
        orgMemberId,
        encryptionCurve,
      );
      await saveInStorage(
        sessionId,
        SharedPreferences.MEMBER_SIGNING_PRIVATE_KEY,
        memberSigningPrivateKey,
      );
      await saveInStorage(
        sessionId,
        SharedPreferences.MEMBER_SIGNING_PUBLIC_KEY,
        lockBoxData.memberSigningPublicKey,
      );

      // const lmsTransectionKey = await generateHMacHash(
      //   orgkey,
      //   "lms" +
      //   (await getFromStorage(sessionId, SharedPreferences.ORG_NAME))
      // );

      // // Decrypt admin private key and save it
      // if (getMemberLockBoxResponse.data.adminPrivateKey) {
      //   const adminPrivateKey = await decryptAsymmetric(getMemberLockBoxResponse.data.adminPrivateKey, memberPrivateKey, getMemberLockBoxResponse.data.adminPrivateKey_kek, orgMemberId);
      //   await saveInStorage(sessionId, SharedPreferences.ADMIN_PRIVATE_KEY, adminPrivateKey);
      // }
    } else {
      throw BayunError.ERROR_USER_INACTIVE;
    }
  } catch (error) {
    const errorData = error?.response?.data;
    if (errorData) {
      console.error("getMemberLockBox failed", {
        status: error?.response?.status,
        errorType: errorData.errorType,
        errorMessage: errorData.errorMessage,
      });
      throw getErrorMessage(sessionId, errorData);
    }
    console.error("getMemberLockBox failed", { message: error?.message, error });
    throw error;
  }
};

/**
 * This method calls the API to save the device info on LMS.
 *
 * @param {String} sessionId id of the session in question.
 */
export async function saveDeviceInfo(sessionId) {
  try {
    // create deviceKey
    const deviceKey = await generateAesKey();
    const baseId =
      (await getFromStorage(sessionId, SharedPreferences.ORG_NAME)) +
      (await getFromStorage(sessionId, SharedPreferences.ORG_MEMBER_ID)) +
      (await getFromStorage(sessionId, SharedPreferences.APP_ID));

    const adminPublicKey = await getFromStorage(
      sessionId,
      SharedPreferences.ADMIN_PUBLIC_KEY,
    );

    const memberPublicKey = await getFromStorage(
      sessionId,
      SharedPreferences.MEMBER_PUBLIC_KEY,
    );
    if (!memberPublicKey) {
      throw BayunError.ERROR_MEMBER_PUBLIC_KEY_NOT_EXISTS;
    }

    const encodedDeviceKey = encodeKey(deviceKey);

    const adminPublicKeyEncryption = await encryptAsymmetric(
      encodedDeviceKey,
      adminPublicKey,
      baseId,
      await getEncryptionCurveFromStorage(sessionId),
    );

    let adminPublicKeyEncrypted = "";
    let adminPublicKeyEncryptedKek = "";

    if (adminPublicKeyEncryption) {
      adminPublicKeyEncrypted = adminPublicKeyEncryption.encryptedText;
      adminPublicKeyEncryptedKek = adminPublicKeyEncryption.keyEncryptionKey;
    }

    const memberPublicKeyEncryption = await encryptAsymmetric(
      encodedDeviceKey,
      memberPublicKey.split(Constants.SIGNATURE_SEPARATOR)[0],
      baseId,
    );

    let memberEncryptedDeviceKey = "";
    let memberEncryptedDeviceKey_kek = "";

    if (memberPublicKeyEncryption) {
      memberEncryptedDeviceKey = memberPublicKeyEncryption.encryptedText;
      memberEncryptedDeviceKey_kek = memberPublicKeyEncryption.keyEncryptionKey;
    }

    // create a rest client
    const authRestClient = await getAuthRestclient(sessionId);

    // call the API
    const saveDeviceInfoResponse = await authRestClient.post("/device", {
      adminEncryptedDeviceKey: adminPublicKeyEncrypted,
      adminEncryptedDeviceKey_kek: adminPublicKeyEncryptedKek,
      memberEncryptedDeviceKey,
      memberEncryptedDeviceKey_kek,
      brand: await aeadEncryptWithAssociatedData(
        navigator.vendor,
        baseId,
        deviceKey,
      ),
      manufacturer: await aeadEncryptWithAssociatedData(
        navigator.platform,
        baseId,
        deviceKey,
      ),
      operatingSystem: await aeadEncryptWithAssociatedData(
        navigator.userAgent,
        baseId,
        deviceKey,
      ),
      uniqueDeviceId: await aeadEncryptWithAssociatedData(
        navigator.appVersion,
        baseId,
        deviceKey,
      ),
    });

    // console.log(saveDeviceInfoResponse);
    await logApiResponse("saveDeviceInfoResponse", saveDeviceInfoResponse);
  } catch (error) {
    console.log(error);
    throw getErrorMessage(sessionId, error?.response?.data ?? error);
  }
}

// Registered rather than imported by memberStatusRefresh: that module is reached from the rest
// client this file imports, so a static import back would close the cycle.
setMemberLockBoxReader(getMemberLockBox);
